import { SourceEntity } from '../../../source/source-entity';
import {
  ISO_DATE_TIME_FORMAT,
  Timestamp,
  Timezone,
  TypeGuard,
  toTimestamp,
} from '@principle-theorem/shared';
import { isNumber, isString } from 'lodash';
import { BaseSourceEntity } from '../../../source/base-source-entity';
import * as moment from 'moment-timezone';
import {
  ISourceEntityTypes,
  SourceEntityMigrationType,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  STERILISATION_CYCLE_DESTINATION_ENTITY,
  STERILISATION_CYCLE_RESOURCE_TYPE,
} from '../../../destination/entities/sterilisation-cycle';

export const STERILISATION_CYCLE_SOURCE_ENTITY = SourceEntity.init({
  metadata: {
    label: 'Sterilisation Cycle List',
    description: '',
    idPrefix: STERILISATION_CYCLE_RESOURCE_TYPE,
    migrationType: SourceEntityMigrationType.Automatic,
  },
});

export enum D4WSterilisationCycleTestStatus {
  Tested = 0,
  NotTested = 1,
  Empty = 2,
}

export interface ID4WSterilisationCycle {
  id: number;
  cycleId: string;
  machineId: number;
  stafferId: number;
  runDate: string;
  isTested: D4WSterilisationCycleTestStatus;
  isPassed: D4WSterilisationCycleTestStatus;
  notes: string;
}

export interface ID4WSterilisationCycleTranslations {
  runDate: Timestamp;
}

export function isD4WSterilisationCycle(
  item: unknown,
  logError: boolean
): item is ID4WSterilisationCycle {
  return TypeGuard.interface<ID4WSterilisationCycle>(
    {
      id: isNumber,
      cycleId: isString,
      machineId: isNumber,
      stafferId: isNumber,
      runDate: isString,
      isTested: TypeGuard.enumValue(D4WSterilisationCycleTestStatus),
      isPassed: TypeGuard.enumValue(D4WSterilisationCycleTestStatus),
      notes: isString,
    },
    logError
  )(item);
}

const STERILISATION_CYCLE_SOURCE_QUERY = `
SELECT
  id_cycle AS id,
  id_sterilizer AS machine_id,
  id_staff AS staffer_id,
  datetime1 AS run_date,
  is_tested,
  is_passed,
  notes,
  convert_to_text(cycle_id_name) AS cycle_id
FROM ast_cycles
WHERE id_cycle IS NOT NULL`;

export type D4WSterilisationCycle = ISourceEntityTypes<
  ID4WSterilisationCycle,
  ID4WSterilisationCycleTranslations
>;

export class SterilisationCyclesSourceEntity extends BaseSourceEntity<D4WSterilisationCycle> {
  sourceEntity = STERILISATION_CYCLE_SOURCE_ENTITY;
  entityResourceType = STERILISATION_CYCLE_RESOURCE_TYPE;
  sourceQuery = STERILISATION_CYCLE_SOURCE_QUERY;

  verifySourceFn = isD4WSterilisationCycle;

  migrationDestinations = [STERILISATION_CYCLE_DESTINATION_ENTITY.metadata.key];

  override translate(
    sterilisationCycle: ID4WSterilisationCycle,
    timezone: Timezone
  ): ID4WSterilisationCycleTranslations {
    return {
      runDate: toTimestamp(
        moment.tz(sterilisationCycle.runDate, ISO_DATE_TIME_FORMAT, timezone)
      ),
    };
  }

  getSourceRecordId(data: ID4WSterilisationCycle): number {
    return data.id;
  }

  getSourceLabel(data: ID4WSterilisationCycle): string {
    return data.cycleId;
  }
}
